import { useEffect, useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";
import { Minus, Plus } from "lucide-react";
import { resolveImageUrl } from "../components/ImagePicker";

export default function ProductDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      setActiveImage(r.data.image_url);
    }).catch(() => {});
  }, [id]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const arr = [product.image_url, ...((product.additional_images || []).filter(Boolean))];
    // Unique
    return [...new Set(arr)];
  }, [product]);

  useEffect(() => {
    if (!product) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-jsonld";
    const url = `${window.location.origin}/product/${product.id}`;
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name[lang] || product.name.en,
      description: product.description[lang] || product.description.en,
      image: gallery.map(resolveImageUrl),
      sku: product.id,
      category: product.category,
      brand: { "@type": "Brand", name: "@pikkurumiluksia" },
      offers: {
        "@type": "Offer",
        url,
        priceCurrency: "EUR",
        price: product.price.toFixed(2),
        availability:
          product.stock > 0
            ? "https://schema.org/InStock"
            : "https://schema.org/OutOfStock",
        itemCondition: "https://schema.org/NewCondition",
        seller: { "@type": "Organization", name: "@pikkurumiluksia" },
      },
    });
    document.head.appendChild(script);
    return () => {
      const existing = document.getElementById("product-jsonld");
      if (existing) existing.remove();
    };
  }, [product, lang, gallery]);

  useEffect(() => {
    if (!product) return;
    const prevTitle = document.title;
    document.title = `${product.name[lang] || product.name.en} — @pikkurumiluksia`;
    return () => { document.title = prevTitle; };
  }, [product, lang]);

  if (!product) {
    return (
      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-24">
        <div className="text-muted-foreground">Loading…</div>
      </main>
    );
  }

  return (
    <main className="max-w-[1400px] mx-auto px-6 md:px-12 pt-10 pb-24 fade-in" data-testid="product-detail">
      <Link to="/shop" className="label-caps text-muted-foreground link-underline inline-block mb-10" data-testid="back-to-shop">
        {t("product.back")}
      </Link>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <div className="product-image-wrap aspect-[4/5] mb-4">
            <img
              src={resolveImageUrl(activeImage || product.image_url)}
              alt={product.name[lang]}
              className="w-full h-full object-cover"
              data-testid="main-image"
            />
          </div>
          {gallery.length > 1 && (
            <div className="grid grid-cols-5 gap-3" data-testid="gallery-thumbs">
              {gallery.map((url, i) => (
                <button
                  key={url}
                  onClick={() => setActiveImage(url)}
                  className={`product-image-wrap aspect-square transition-all ${
                    activeImage === url
                      ? "ring-2 ring-[hsl(var(--primary))] ring-offset-2 ring-offset-background"
                      : "opacity-70 hover:opacity-100"
                  }`}
                  data-testid={`thumb-${i}`}
                  aria-label={`Kuva ${i + 1}`}
                >
                  <img src={resolveImageUrl(url)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="lg:col-span-5 lg:pt-8">
          <div className="label-caps text-muted-foreground mb-4">{product.category}</div>
          <h1 className="font-hand text-5xl md:text-6xl leading-tight mb-6" data-testid="detail-name">{product.name[lang]}</h1>
          <div className="font-hand text-4xl mb-8 text-[hsl(var(--primary))]" data-testid="detail-price">
            {product.price.toFixed(2).replace(".", ",")} €
          </div>
          <p className="text-base text-muted-foreground leading-relaxed mb-10">
            {product.description[lang]}
          </p>

          <div className="border-t border-border pt-8 mb-8">
            <label htmlFor="product-note" className="label-caps text-muted-foreground mb-3 block">
              {t("product.noteLabel")}
            </label>
            <textarea
              id="product-note"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              rows={3}
              className="w-full border border-border bg-transparent px-4 py-3 text-sm leading-relaxed focus:outline-none focus:border-[hsl(var(--primary))] transition-colors resize-y"
              data-testid="product-note-input"
            />
            <div className="text-[11px] text-muted-foreground mt-1 text-right">{note.length}/500</div>
          </div>

          <div className="mb-10">
            <div className="label-caps text-muted-foreground mb-4">{t("product.quantity")}</div>
            <div className="flex items-center gap-4">
              <div className="flex items-center border border-border">
                <button
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                  data-testid="qty-decrease"
                >
                  <Minus strokeWidth={1.25} className="w-4 h-4" />
                </button>
                <div className="w-12 text-center font-hand text-2xl" data-testid="qty-value">{qty}</div>
                <button
                  onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))}
                  className="w-10 h-10 flex items-center justify-center hover:bg-secondary transition-colors"
                  data-testid="qty-increase"
                >
                  <Plus strokeWidth={1.25} className="w-4 h-4" />
                </button>
              </div>
              <button
                className="btn-primary"
                onClick={() => {
                  add(product, qty, note);
                  setNote("");
                  toast.success(t("shop.added"));
                }}
                disabled={product.stock === 0}
                data-testid="add-to-cart-btn"
              >
                {product.stock === 0 ? t("shop.outOfStock") : t("shop.add")}
              </button>
            </div>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            <div>· {t("product.handmade")}</div>
            <div>· {t("product.care")}</div>
          </div>
        </div>
      </div>
    </main>
  );
}
